import { Vector2, Vector3 } from "three";
import LengthValue from "../domain/dto/core/Length/LengthValue";
import { LengthValueMeter } from "../domain/dto/core/Length/LengthValueMeter";
import { HitUtils } from "./utils/HitUtils";
import { SwizzleExtension } from "./utils/SwizzleExtension";

export class PolygonIndexer {
    polygon: Vector3[];
    currentIndex: number;

    constructor(polygon: Vector3[], current: number = 0) {
        this.polygon = polygon;
        this.currentIndex = current;
    }

    get total(): number {
        return this.polygon.length;
    }

    get previousIndex(): number {
        let previous = this.currentIndex - 1;
        if (previous < 0)
            previous = this.total - 1;
        return previous;
    }

    get nextIndex(): number {
        let next = this.currentIndex + 1;
        if (next === this.total)
            next = 0;
        return next;
    }

    get currentVector(): Vector3 {
        return this.polygon[this.currentIndex];
    }

    get previousVector(): Vector3 {
        return this.polygon[this.previousIndex];
    }

    get nextVector(): Vector3 {
        return this.polygon[this.nextIndex];
    }

    get currentVectorNormalized(): Vector3 {
        return this.nextVector.clone().sub(this.currentVector).normalize();
    }

    get previousVectorNormalized(): Vector3 {
        return this.currentVector.clone().sub(this.previousVector).normalize();
    }

    getCurrentVectorTranslated(offset: LengthValue | null, usePreviousVector: boolean = false): Vector3 {
        if (!offset)
            return this.currentVector;

        var vectorTmp = (usePreviousVector ? this.previousVectorNormalized : this.currentVectorNormalized).clone();
        vectorTmp.multiplyScalar(offset.meter);
        return this.currentVector.clone().add( vectorTmp );
    }

    getPreviousVectorTranslated(offset: LengthValue | null): Vector3 {
        if (!offset)
            return this.previousVector;

        var vectorTmp = this.previousVectorNormalized.clone();
        vectorTmp.multiplyScalar(offset.meter);
        return this.previousVector.clone().add(vectorTmp);
    }

    increment(): PolygonIndexer {
        this.currentIndex = this.nextIndex;
        return this;
    }

    decrement(): PolygonIndexer {
        this.currentIndex = this.previousIndex;
        return this;
    }

    findIntersections(p1: Vector3, p2: Vector3, returnIntermediaryVectors: boolean = false, limit: number | null = null, extendSegments: boolean = false, extendInputSegment: boolean = false): Vector3[] {
        const intersections: Vector3[] = [];
        const startIndex = this.currentIndex;

        do {

            const xz1 = SwizzleExtension.XZ(this.currentVector);
            const xz2 = SwizzleExtension.XZ(this.previousVector);
            const xz3 = SwizzleExtension.XZ(p1);
            const xz4 = SwizzleExtension.XZ(p2);

            let intersect: Vector2 = new Vector2();

            if (HitUtils.LinesIntersect(xz1, xz2, xz3, xz4, intersect, true, extendSegments, extendInputSegment)) {
                intersections.push(new Vector3(intersect.x, 0, intersect.y));
                if (intersections.length >= (limit || Infinity))
                    return intersections;
            } else if (returnIntermediaryVectors) {
                intersections.push(this.currentVector);
            }

            this.increment();
        } while (this.currentIndex !== startIndex);

        return intersections;
    }

    getAllLengths(): LengthValue[] {
        const startIndex = this.currentIndex;
        const lengths: LengthValue[] = [];

        do {
            lengths.push(new LengthValueMeter(this.currentVector.distanceTo(this.nextVector)));
            this.increment();
        } while (this.currentIndex !== startIndex);

        return lengths;
    }
}
